// useCalibrationLogBookAutosave.js
// Hook for Calibration Log Book autosave with websocket and IndexedDB queue fallback.
// Mirrors useSamplingEventAutosave (no JSA channel).

import { useState, useEffect, useCallback, useRef } from "react";
import {
  initializeCalibrationLogBookSocket,
  joinCalibrationLogBook,
  leaveCalibrationLogBook,
  emitCalibrationLogBookPatch,
  onCalibrationLogBookConnectionChange,
  onCalibrationLogBookUpdate,
  clearCalibrationLogBookHandlers,
} from "../services/calibrationLogBookSocketService";
import {
  queuePatch,
  flushQueue,
  getPendingCount,
} from "../services/calibrationLogBookPatchQueue";
import { useAppStore } from "../store/appStore";

export const useCalibrationLogBookAutosave = (bookId) => {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [roomStatus, setRoomStatus] = useState("idle"); // idle | joining | joined | error
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, saved, queued, error
  const [pendingCount, setPendingCount] = useState(0);
  const joinedRef = useRef(false);
  const bookIdRef = useRef(bookId);

  // Keep bookId ref current
  useEffect(() => {
    bookIdRef.current = bookId;
  }, [bookId]);

  // Initialize socket, join room, and set up handlers
  useEffect(() => {
    if (!bookId) return;

    initializeCalibrationLogBookSocket();
    setRoomStatus("joining");

    const handleConnectionChange = async (connected) => {
      setConnectionStatus(connected ? "connected" : "disconnected");
      if (!connected) {
        joinedRef.current = false;
        setRoomStatus("joining");
      }
      if (connected && bookIdRef.current) {
        try {
          if (!joinedRef.current) {
            setRoomStatus("joining");
            const ack = await joinCalibrationLogBook(bookIdRef.current);
            joinedRef.current = Boolean(ack?.success);
            setRoomStatus(ack?.success ? "joined" : "error");
          }
          const flushed = await flushQueue(
            bookIdRef.current,
            emitCalibrationLogBookPatch
          );
          if (flushed > 0) {
            const remaining = await getPendingCount(bookIdRef.current);
            setPendingCount(remaining);
          }
        } catch (err) {
          console.error("Error during Calibration Log Book socket reconnection:", err);
        }
      }
    };

    // Handle incoming broadcasts - route to store's applyWebsocketPatch
    const handleUpdate = (patchData) => {
      try {
        useAppStore
          .getState()
          .activeCalibrationLogBookActions.applyWebsocketPatch(patchData);
      } catch (e) {
        console.error("Failed to apply calibration log book websocket patch:", e);
      }
    };

    onCalibrationLogBookConnectionChange(handleConnectionChange);
    onCalibrationLogBookUpdate(handleUpdate);

    // Initial join
    joinCalibrationLogBook(bookId)
      .then((ack) => {
        joinedRef.current = Boolean(ack?.success);
        setRoomStatus(ack?.success ? "joined" : "error");
      })
      .catch((err) =>
        console.error("Failed to join Calibration Log Book room:", err)
      );

    // Update pending count
    getPendingCount(bookId)
      .then(setPendingCount)
      .catch((err) => console.error("Failed to get pending patch count:", err));

    return () => {
      if (bookIdRef.current && joinedRef.current) {
        leaveCalibrationLogBook(bookIdRef.current).catch((err) =>
          console.warn("Failed to leave Calibration Log Book room:", err)
        );
        joinedRef.current = false;
      }
      clearCalibrationLogBookHandlers();
    };
  }, [bookId]);

  // Emit patch with queue fallback
  const emitPatch = useCallback(async (patch) => {
    if (!bookIdRef.current) return false;

    setSaveStatus("saving");

    const result = await emitCalibrationLogBookPatch(bookIdRef.current, patch);

    if (!result.error && !result.shouldQueue) {
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
      return true;
    }

    if (result.shouldQueue) {
      try {
        await queuePatch(bookIdRef.current, patch);
        const count = await getPendingCount(bookIdRef.current);
        setPendingCount(count);
      } catch (idbErr) {
        console.warn(
          "Failed to queue Calibration Log Book patch to browser storage.",
          idbErr?.message || idbErr
        );
      }
      setSaveStatus("queued");
    } else {
      setSaveStatus("error");
      console.error("Calibration log book patch validation error:", result.error);
      if (result.error === "Unauthorized") {
        window.location.href = "/login";
      }
      setTimeout(() => setSaveStatus("idle"), 3000);
    }

    return false;
  }, []);

  return {
    emitPatch,
    connectionStatus,
    roomStatus,
    saveStatus,
    pendingCount,
    isConnected: connectionStatus === "connected",
  };
};